/**
 * Resolvedor ÚNICO de authz del cliente — núcleo de `@trud/auth` (trd-client#49,
 * master plan trudapp/trd-platform#14 Fase 5).
 *
 * PROBLEMA QUE RESUELVE: hoy el front decide qué mostrar con su propia lógica
 * dispersa (plan-gate por `businessPlanFeatures`, owner-bypass ad-hoc,
 * `checkIfHasPermission` duplicado) que NO coincide con lo que el BORDE (el PEP
 * del BFF) realmente permite. Resultado: el menú ofrece módulos que después
 * responden 403 (el caso testigo es Veterinaria: el owner la ve y el clinical
 * wall se la niega).
 *
 * ESTE MÓDULO REPLICA LA DECISIÓN DEL BORDE (`trd-bff/src/shared/authz.rs::
 * local_decision`), que decide SÓLO con `modules` (los `enabled_resources` de
 * identity) — NUNCA con `businessPlanFeatures`. Mismo orden:
 *   1. clinical wall — `clinical`/`clinical/*` sin `is_clinician` => DENY (le gana
 *                      AL OWNER, ADR-0002), pero SÓLO en salud humana; en
 *                      veterinaria (`organization.humanHealth === false`) no aplica
 *                      —no es PHI humano— y el owner de la vet ve lo clínico
 *   2. entitlement   — el recurso está habilitado para el negocio (`modules`).
 *                      Aplica a TODOS: el borde deja pasar al owner sin límite,
 *                      pero para el MENÚ el cliente nunca ofrece MÁS que el borde
 *                      (dirección segura: mostramos ≤ lo que el borde permite).
 *   3. owner         — allow sin grant, salvo claim suspended (trd-bff#303)
 *   4. empleado      — allow sólo si el grant tiene el verbo pedido; con el
 *                      negocio suspendido, sólo lee (trd-client#269)
 *
 * El eje COMERCIAL — ¿el PLAN contrató la feature? — es OTRA cosa: NO pasa por el
 * borde (el PEP sólo mira `modules`+grant) y vive aparte, en `hasBusinessFeature`
 * (`businessPlanFeatures`), que gatea ítems `planFeature` del menú (Chat, tienda
 * online). Meter `businessPlanFeatures` en la decisión de RECURSO era la
 * divergencia que rompía la clínica: identity refinó `modules`/grant a
 * `clinical/*` pero `businessPlanFeatures` sigue en el dialecto viejo
 * `veterinary/*`, así que el plan-gate la negaba aunque el borde la permitiera.
 *
 * DEGRADACIÓN CONSERVADORA: si un dato no está disponible (p.ej. el `/me` aún no
 * expone `is_clinician`, o no hay `modules`), esa regla NO restringe. Nunca
 * escondemos de más por falta de información.
 *
 * Es una función PURA (sin React, sin red) a propósito: es lo que se extrae tal
 * cual a `@trud/auth-core` y lo que permite testearla sin browser.
 */

/** Verbos canónicos de identity (NO las letras r/w/d del modelo viejo). */
export type AuthzAction = "view" | "modify" | "delete";

/** Lo que el resolvedor necesita saber del usuario (mezcla `/me` + claims). */
export interface AuthzSubject {
  /** "owner" | "employee" | … */
  type?: string | null;
  /** Grant por IdPath: `{ "catalog/products": ["view","modify"] }`. */
  permissions?: Record<string, string[]> | null;
  /**
   * Entitlement del negocio (`enabled_resources` de identity) — lo que el BORDE
   * enforca Y la ÚNICA fuente de features de plan tras el reshape del /me
   * (reemplazó a `businessPlanFeatures`). Lo usan `resolveModuleAccess` y `hasModule`.
   */
  modules?: string[] | null;
  /** Del token (`is_clinician`). `undefined` => desconocido => no restringe. */
  isClinician?: boolean | null;
  /** Claim explícito del BFF. Nunca inferido de dunning ni de módulos vacíos. */
  suspended?: boolean;
  /**
   * Org del /me. `humanHealth` gatea el MURO clínico por vertical: `false` =
   * veterinaria => SIN muro (los datos no son PHI humano); `true`/ausente =>
   * muro activo (salud humana o desconocido => protegemos PHI).
   */
  organization?: { humanHealth?: boolean | null } | null;
}

export type AuthzDenyReason =
  | "no_user"
  | "suspended"
  | "clinical_wall"
  | "not_entitled"
  | "no_grant";

export interface AuthzDecision {
  allowed: boolean;
  reason?: AuthzDenyReason;
}

const ALLOW: AuthzDecision = { allowed: true };
const deny = (reason: AuthzDenyReason): AuthzDecision => ({
  allowed: false,
  reason,
});

/**
 * Superficie clínica, igual que el borde (`is_high_stakes`/clinical wall):
 * `resource == "clinical" || resource.starts_with("clinical/")`.
 */
export function isClinicalModule(moduleId?: string | null): boolean {
  if (!moduleId) return false;
  // `veterinary/*` murió (platform#20); la superficie clínica es `clinical`
  // (plano, deprecado) y `clinical/*` (refinado: patients|visits|practitioners).
  return moduleId === "clinical" || moduleId.startsWith("clinical/");
}

/**
 * ¿Puede el sujeto hacer `action` sobre `moduleId`?
 *
 * `moduleId` vacío/ausente => ALLOW (ítems que no cuelgan de un módulo, tipo
 * "free"). Ver el orden de reglas en el docblock del archivo.
 */
export function resolveModuleAccess(
  subject: AuthzSubject | null | undefined,
  moduleId?: string | null,
  action: AuthzAction = "view"
): AuthzDecision {
  if (!subject) return deny("no_user");
  if (!moduleId) return ALLOW;
  if (subject.type === "owner" && subject.suspended === true)
    return deny("suspended");
  // El empleado de un negocio suspendido ve la app en modo lectura: lee lo que
  // sus módulos y su grant le dejan, y no escribe (laudo de trd-octopus#333,
  // trd-client#269). Las escrituras las rechaza el borde (trd-bff#333); acá se
  // refleja para no ofrecerle lo que va a rebotar. Con el flag de octopus
  // apagado los módulos ya vienen recortados y le niegan también las lecturas.
  if (subject.suspended === true && action !== "view") return deny("suspended");

  // 1) Clinical wall (PHI, ADR-0002): le gana al owner. Pero sólo en salud
  //    HUMANA. En veterinaria (`organization.humanHealth === false`) NO hay muro
  //    —los datos no son PHI humano— así el owner de la vet ve lo clínico (se
  //    gatea normal: modules + owner/grant). Espeja a identity (autoridad de lo
  //    clínico vía Cedar), que exime a las vet por `org_human_health`. Conservador:
  //    `humanHealth` ausente o `true` => muro activo (no abrimos PHI por falta de
  //    dato). Sólo restringe si además SABEMOS que no es clínico (`false`).
  if (
    isClinicalModule(moduleId) &&
    subject.isClinician === false &&
    subject.organization?.humanHealth !== false
  ) {
    return deny("clinical_wall");
  }

  // 2) Entitlement: el recurso tiene que estar habilitado para el negocio
  //    (`modules` = `enabled_resources`, lo que enforca el borde). Aplica a
  //    TODOS incl. owner: el menú no ofrece módulos no habilitados. NO se mira
  //    `businessPlanFeatures` (el borde tampoco; es el eje comercial, ver
  //    `hasBusinessFeature`). `modules` ausente/vacío => no restringe.
  const modules = subject.modules ?? [];
  if (modules.length > 0 && !modules.includes(moduleId)) {
    return deny("not_entitled");
  }

  // 3) Owner: allow, sin chequeo de grant (su alcance no lo limita el grant).
  if (subject.type === "owner") return ALLOW;

  // 4) Empleado: el grant para este IdPath tiene que traer el verbo pedido.
  if (subject.permissions) {
    const grant = subject.permissions[moduleId] ?? [];
    if (!grant.includes(action)) return deny("no_grant");
  }

  return ALLOW;
}

/**
 * ¿Puede el sujeto hacer `action` sobre AL MENOS UNO de `resource`?
 *
 * Envoltura booleana de `resolveModuleAccess` — es el **único** punto de decisión
 * de authz del front, keyed por **resource id del entitlement** (NUNCA por strings
 * de ruta, que fue la fuente de la divergencia con `paths.ts`). Es el ESPEJO del
 * `can` del borde (trd-bff, el PEP real); mismo vocab `view/modify/delete`, misma
 * lógica. El front sólo refleja para UX — el gate real es el BFF.
 *
 * `resource` acepta uno o varios ids con semántica "cualquiera" (un ítem se ve si
 * el sujeto puede sobre alguno). `resource` vacío/ausente => ALLOW (ítems que no
 * cuelgan de ningún recurso). Extraíble tal cual a `@trud/auth-core` (#49).
 */
export function can(
  subject: AuthzSubject | null | undefined,
  resource?: string | string[] | null,
  action: AuthzAction = "view"
): boolean {
  const ids =
    resource == null ? [] : Array.isArray(resource) ? resource : [resource];
  if (ids.length === 0)
    return resolveModuleAccess(subject, null, action).allowed;
  return ids.some((id) => resolveModuleAccess(subject, id, action).allowed);
}

/**
 * El corte "sólo el dueño" de una pantalla o de un ítem del menú (`ownerOnly`).
 *
 * **Se suma** a la decisión que ya se tomó (`allowed`), no la reemplaza: el dueño
 * igual tiene que pasar `can()`, que es fail-closed también para él —un dueño
 * sin el módulo no entra—. Por eso no alcanza con el viejo
 * `permissionLink: "owner"` del menú, que miraba sólo el rol.
 *
 * Una sola función para el menú y para la ruta: si el menú escondiera la
 * entrada y la ruta no, el grant del empleado seguiría abriendo la pantalla por
 * URL (trd-client#285).
 */
export function withOwnerOnly(
  subject: AuthzSubject | null | undefined,
  ownerOnly: boolean | undefined,
  allowed: boolean
): boolean {
  return allowed && (!ownerOnly || subject?.type === "owner");
}

/**
 * ¿El NEGOCIO tiene habilitado el módulo/feature? Entitlement a nivel NEGOCIO
 * contra `modules` (`enabled_resources` de identity) — la ÚNICA fuente de
 * entitlement tras el reshape del /me (`businessPlanFeatures` se eliminó, ver
 * `me_types.rs` / trd-core#954). Reemplaza al viejo `hasBusinessFeature` (que
 * miraba `businessPlanFeatures`) para gatear features de plan del menú y de
 * side-effects (Chat, tienda, warehouses…).
 *
 * NO mira el grant per-user (para eso está `can`) ni hace owner-bypass: una feature
 * la tiene el NEGOCIO o no, sin importar el rol. `feature` vacía/ausente => `true`
 * (ítems sin gate). `modules` ausente/vacío => `false` (no asumimos entitlement por
 * falta de dato; degradación conservadora para FEATURES, al revés que el menú).
 *
 * ⚠️ Sólo sirve para features que SON recursos del entitlement (`modules`). Las
 * comerciales puras (addons como `ai/chat`, `ecommerce/storefront`) NO están hoy en
 * `modules` — el BFF tiene que incluirlas ahí (o resolverlas por addons) o quedan
 * en `false`.
 */
export function hasModule(
  subject: AuthzSubject | null | undefined,
  feature?: string | null
): boolean {
  if (!feature) return true;
  return (subject?.modules ?? []).includes(feature);
}
